import { GraduationCap, Puzzle, HeartPulse } from "lucide-react";
import PageHero, { NextPage } from "@/components/PageHero";
import { FadeUp, StaggerGroup, StaggerItem } from "@/components/Reveal";

const STUDY_IMG =
  "https://images.unsplash.com/photo-1644416598043-11c2816eec28";

const PROVISION = [
  {
    title: "Education",
    Icon: GraduationCap,
    color: "#FF508D",
    copy: "Quarrendon School on Weedon Road is a local secondary school. For children aged 14 and over, Aylesbury UTC on Oxford Road offers a technical pathway, while Buckinghamshire College Group's Aylesbury Campus supports future vocational planning. Any placement remains subject to admissions, suitability and the placing authority's decision.",
    testid: "education-card",
  },
  {
    title: "SEND & alternative",
    Icon: Puzzle,
    color: "#FFB019",
    copy: "Staff will implement EHCP and PEP strategies, make reasonable adjustments, adapt communication and work with the SENCO and Virtual School. Aylesbury also has specialist provision, including Pebble Brook and Stocklake Park for ages 11-19; these are local examples only and are not guaranteed placements.",
    testid: "send-card",
  },
  {
    title: "Health",
    Icon: HeartPulse,
    color: "#696DFF",
    copy: "Local resources include Tesco Pharmacy at Broadfields Retail Park, Meadowcroft Pharmacy and dental services on Buckingham Road. A GP will be selected according to catchment and availability. Stoke Mandeville Hospital provides local hospital services and a 24-hour accident and emergency department.",
    testid: "health-card",
  },
];

const GOALS = [
  { title: "Safety and stability", copy: "Fewer crises, stronger routines and an increased sense of security.", color: "#FF508D" },
  { title: "Emotional wellbeing", copy: "Greater self-awareness, coping skills and access to therapeutic support.", color: "#FF9137" },
  { title: "Education and achievement", copy: "Consistent attendance, personalised learning and recognised progress.", color: "#5CFFAF" },
  { title: "Relationships and identity", copy: "Safe connections, respect, belonging and confidence in who he is.", color: "#696DFF" },
  { title: "Independence", copy: "Age-appropriate skills in self-care, travel, budgeting, decision-making and community life.", color: "#FF508D" },
];

export default function EducationOutcomes() {
  return (
    <div data-testid="page-education-outcomes">
      <PageHero
        testid="education-hero"
        eyebrow="A connected local network"
        title={[
          "Education, health and",
          <>
            <em className="text-rainbow italic">positive outcomes</em>
          </>,
        ]}
        lead="Education and health arrangements will be agreed for the individual child rather than assumed from proximity. The placing authority, Virtual School, education provider and relevant health professionals will remain central to assessment, commissioning and review."
      />

      {/* Local provision */}
      <section className="container-x" data-testid="provision-section">
        <StaggerGroup className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {PROVISION.map(({ title, Icon, color, copy, testid }) => (
            <StaggerItem key={title}>
              <div className="card h-full p-8 transition-[transform,box-shadow] duration-200 hover:-translate-y-1 hover:shadow-[0_24px_45px_-28px_rgba(45,55,72,0.45)]" data-testid={testid}>
                <div className="flex items-center justify-between">
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-sand text-ink">
                    <Icon className="h-6 w-6" strokeWidth={1.5} />
                  </span>
                  <span className="dot" style={{ background: color }} aria-hidden="true" />
                </div>
                <h3 className="mt-6 font-display text-2xl text-ink">{title}</h3>
                <p className="mt-3 text-base leading-relaxed text-mute">{copy}</p>
              </div>
            </StaggerItem>
          ))}
        </StaggerGroup>
      </section>

      {/* Coordination */}
      <section className="container-x py-16 sm:py-20 lg:py-24" data-testid="coordination-section">
        <div className="grid items-center gap-10 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <FadeUp>
              <p className="eyebrow">Coordination</p>
              <h2 className="mt-4 font-display text-3xl tracking-tight text-ink sm:text-4xl" data-testid="coordination-title">
                How education and health will be coordinated
              </h2>
              <p className="mt-6 text-lg leading-relaxed text-mute" data-testid="coordination-copy">
                The Registered Manager will ensure that statutory plans, appointments and
                professional recommendations are translated into daily practice. Staff will support
                attendance, transport, homework, revision, health appointments and prescribed
                treatment. Jazzino Davies will contribute therapeutic formulation and planned work,
                while safeguarding and clinical concerns will continue to be referred through the
                appropriate statutory services.
              </p>
            </FadeUp>
          </div>
          <FadeUp delay={0.15} className="lg:col-span-6">
            <div className="overflow-hidden rounded-[2rem] border border-line shadow-[0_30px_60px_-35px_rgba(45,55,72,0.4)]">
              <img
                src={STUDY_IMG}
                alt="A display of modern children's picture books"
                className="h-[300px] w-full object-cover sm:h-[420px]"
                data-testid="study-image"
              />
            </div>
          </FadeUp>
        </div>
      </section>

      {/* What we work towards */}
      <section className="container-x" data-testid="goals-section">
        <FadeUp>
          <p className="eyebrow">What we work towards</p>
          <h2 className="mt-4 max-w-2xl font-display text-3xl tracking-tight text-ink sm:text-4xl" data-testid="goals-title">
            Five outcomes, evidenced every day
          </h2>
        </FadeUp>
        <StaggerGroup className="mt-12">
          {GOALS.map(({ title, copy, color }, i) => (
            <StaggerItem key={title}>
              <div className="grid items-center gap-4 border-t border-line py-8 sm:grid-cols-12 sm:gap-8" data-testid={`goal-${i + 1}`}>
                <p className="font-display text-5xl tracking-tight sm:col-span-2" style={{ color }} data-testid={`goal-number-${i + 1}`}>
                  {String(i + 1).padStart(2, "0")}
                </p>
                <div className="sm:col-span-10">
                  <h3 className="font-display text-2xl text-ink">{title}</h3>
                  <p className="mt-2 max-w-2xl text-base leading-relaxed text-mute">{copy}</p>
                </div>
              </div>
            </StaggerItem>
          ))}
        </StaggerGroup>
        <FadeUp>
          <div className="border-t border-line pt-10">
            <div className="card bg-sand p-8 sm:p-10" data-testid="evidence-card">
              <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-mute">How progress is evidenced</p>
              <p className="mt-4 max-w-4xl text-base leading-relaxed text-ink/90" data-testid="evidence-copy">
                Progress will be evidenced through daily records, key-work, education and health
                information, incident analysis, professional feedback and the child's own account of
                his experience. Local services and admission arrangements will be rechecked at
                referral and throughout the placement.
              </p>
            </div>
          </div>
        </FadeUp>
      </section>

      <div className="container-x pb-4 pt-16 sm:pt-20">
        <NextPage
          to="/team"
          title="Leadership and governance"
          label="The people accountable from provider to practice"
          testid="next-team"
        />
      </div>
    </div>
  );
}
